import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';

/**
 * ProfileOnboarding - Shows a modal when user's profile is incomplete
 *
 * Triggered when:
 * - User has no first_name or last_name set
 * - User has tenant memberships without display_name
 *
 * For Google OAuth users: first/last name is auto-populated from Google
 * For OTP users: They need to enter their name on first login
 */
const ProfileOnboarding = ({ auth }) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameInSync, setDisplayNameInSync] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [hasTenants, setHasTenants] = useState(false);

  // Track the expected auto-generated display name
  const expectedDisplayName = useRef('');

  // Check profile status on mount and when auth changes
  useEffect(() => {
    if (auth && auth.id) {
      checkProfileStatus();
    }
  }, [auth?.id]);

  // Auto-populate display name when first/last name changes (if still in sync)
  useEffect(() => {
    const autoName = `${firstName.trim()} ${lastName.trim()}`.trim();
    expectedDisplayName.current = autoName;

    if (displayNameInSync) {
      setDisplayName(autoName);
    }
  }, [firstName, lastName, displayNameInSync]);

  const checkProfileStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/user/profile-status', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // Show modal if profile is incomplete
        if (!data.profileComplete) {
          setFirstName(data.firstName || '');
          setLastName(data.lastName || '');
          setHasTenants(data.tenantsNeedingDisplayName?.length > 0);

          // Initialize display name
          const initialDisplayName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
          setDisplayName(initialDisplayName);
          expectedDisplayName.current = initialDisplayName;

          setShow(true);
        }
      }
    } catch (err) {
      console.error('Error checking profile status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayNameChange = (e) => {
    const newValue = e.target.value;
    setDisplayName(newValue);

    // Check if display name is still in sync with first+last
    const autoName = `${firstName.trim()} ${lastName.trim()}`.trim();
    setDisplayNameInSync(newValue === autoName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    if (hasTenants && !displayName.trim()) {
      setError('Please enter a display name');
      return;
    }

    try {
      setSaving(true);

      const response = await fetch('/api/user/profile/complete', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          display_name: displayName.trim() || `${firstName.trim()} ${lastName.trim()}`
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Check if there were any display name conflicts
        if (data.hasConflicts) {
          setConflicts(data.displayNames.filter(d => d.isConflict));
        } else {
          setShow(false);
          // Reload the page to refresh auth state
          window.location.reload();
        }
      } else {
        setError(data.error || 'Failed to save profile');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConflictAcknowledge = () => {
    setShow(false);
    // Reload to refresh auth state
    window.location.reload();
  };

  // Don't render anything if not logged in or still loading
  if (!auth || !auth.id || loading) {
    return null;
  }

  // Don't show on login or public pages
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path === '/login' || path === '/' || path.startsWith('/free')) {
      return null;
    }
  }

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          {conflicts.length > 0 ? 'Welcome!' : 'Complete Your Profile'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {conflicts.length > 0 ? (
          // Display name conflict notice
          <div>
            <Alert variant="info">
              Your profile has been saved! However, your preferred display name was
              already taken in one or more organizations:
            </Alert>
            <ul className="mb-3">
              {conflicts.map((c, idx) => (
                <li key={idx}>
                  <strong>{c.tenantName}</strong>: Your display name was set to "{c.displayName}"
                </li>
              ))}
            </ul>
            <p className="text-muted mb-0">
              You can change your display name anytime in Settings.
            </p>
          </div>
        ) : (
          // Profile form
          <Form onSubmit={handleSubmit}>
            <p className="text-muted mb-4">
              Please tell us your name to get started.
              {hasTenants && ' This will be visible to others in your organization.'}
            </p>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>First Name *</Form.Label>
              <Form.Control
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                autoFocus
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Last Name *</Form.Label>
              <Form.Control
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                disabled={saving}
              />
            </Form.Group>

            {hasTenants && (
              <Form.Group className="mb-3">
                <Form.Label>Display Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={displayName}
                  onChange={handleDisplayNameChange}
                  placeholder="How you'll appear in your organization"
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  This is how others in your organization will see you.
                  {displayNameInSync && ' Auto-populated from your name.'}
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {conflicts.length > 0 ? (
          <Button variant="primary" onClick={handleConflictAcknowledge}>
            Got it, continue
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !firstName.trim() || !lastName.trim() || (hasTenants && !displayName.trim())}
          >
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(ProfileOnboarding);
